import { useState, useCallback } from "react";

const STAGES = [
  { key:"pcp", label:"Product, Configuration & Partnerisation" },
  { key:"fin", label:"Financing" },
  { key:"bp",  label:"Bundled Proposition" },
  { key:"ena", label:"Enablement" },
  { key:"acq", label:"Acquisition" },
  { key:"fsa", label:"Fulfilment, Service & Assurance" },
  { key:"bca", label:"Billing & Commercial Administration" },
];

// Each activity has a label and an implied ownership score (1=mostly partner … 5=fully Level)
const ACTIVITIES = {
  perksco: {
    pcp: [{l:"Catalogue design",s:5},{l:"Merchant onboarding logic",s:5},{l:"White-label setup",s:4},{l:"Partner environment integration",s:3},{l:"Rules and permissions",s:4},{l:"Dashboard / reporting setup",s:4}],
    fin: [{l:"Financing structure for selected marketplace items",s:5},{l:"Affordability and collection rules",s:5}],
    bp:  [{l:"Combine marketplace with EWA, healthcare, savings, or wellness",s:4},{l:"Segment offers by employer and employee needs",s:3}],
    ena: [{l:"Partner team training",s:4},{l:"Demo environment",s:4},{l:"Product collateral",s:4},{l:"Operational workflow training",s:3}],
    acq: [{l:"Campaigns",s:2},{l:"Lead introductions",s:1},{l:"Joint meetings",s:2},{l:"Proposals and solutioning",s:2}],
    fsa: [{l:"Employer setup",s:4},{l:"Merchant / service coordination",s:4},{l:"Support",s:3},{l:"Service monitoring",s:4},{l:"Issue resolution",s:3}],
    bca: [{l:"Invoicing",s:4},{l:"Commission splits",s:5},{l:"Reconciliation",s:4},{l:"Usage reporting",s:4}],
  },
  flexpay: {
    pcp: [{l:"Payroll integration",s:5},{l:"Access rules",s:4},{l:"Caps and cut-off windows",s:4},{l:"Policy configuration",s:4},{l:"Employer-specific controls",s:3}],
    fin: [{l:"Capital provision",s:5},{l:"Advance funding",s:5},{l:"Liquidity management",s:5},{l:"Underwriting logic",s:5},{l:"Fraud and loss controls",s:5},{l:"Settlement design",s:5}],
    bp:  [{l:"Link to Atomic, education, or savings",s:4},{l:"Structure as entry benefit into broader stack",s:4}],
    ena: [{l:"Employer admin training",s:3},{l:"Payroll process training",s:3},{l:"Partner sales enablement",s:4},{l:"Demo setup",s:4}],
    acq: [{l:"Employer targeting",s:2},{l:"Partner-led sales",s:1},{l:"Solutioning support",s:3},{l:"ROI-based sell-in",s:2}],
    fsa: [{l:"Employer rollout",s:4},{l:"Payroll testing",s:5},{l:"Employee activation",s:4},{l:"Advance processing support",s:5},{l:"Complaints and issue handling",s:3}],
    bca: [{l:"Employer fees",s:4},{l:"Settlement reports",s:5},{l:"Revenue-share reports",s:4},{l:"Reconciliations",s:4}],
  },
  savings: {
    pcp: [{l:"Payroll-linked contribution setup",s:5},{l:"Prize-linked mechanics setup",s:5},{l:"Contribution rules",s:4},{l:"Partner / channel branding",s:3},{l:"Member reporting",s:4}],
    fin: [{l:"Reserve management",s:5},{l:"Prize pool management",s:5},{l:"Treasury handling",s:5},{l:"Settlement of savings allocations",s:5}],
    bp:  [{l:"Combine with wellness journeys",s:3},{l:"Combine with EWA as resilience path",s:3},{l:"Combine with rewards and employer contributions",s:3}],
    ena: [{l:"Partner training",s:4},{l:"Member education toolkit",s:4},{l:"Campaign toolkits",s:3},{l:"FAQ and onboarding material",s:4}],
    acq: [{l:"Savings campaigns",s:2},{l:"Awareness drives",s:2},{l:"Employer onboarding support",s:2},{l:"Participation drives",s:2}],
    fsa: [{l:"Member registration",s:4},{l:"Payroll deduction support",s:4},{l:"Contribution processing",s:5},{l:"Draw administration",s:5},{l:"Support and service resolution",s:3}],
    bca: [{l:"Fee charging",s:4},{l:"Prize / reward reconciliation",s:5},{l:"Payroll deduction reconciliation",s:4},{l:"Reporting",s:4}],
  },
  simcare: {
    pcp: [{l:"Configure consult, diagnostics, medication, insurance journeys",s:5},{l:"Provider structure",s:5},{l:"Funding model setup",s:4},{l:"Employer-specific benefit rules",s:3},{l:"Channel integration",s:4}],
    fin: [{l:"Healthcare credit funding",s:5},{l:"Deferred payment structures",s:5},{l:"Provider settlement rules",s:5},{l:"Affordability and collections design",s:5}],
    bp:  [{l:"Combine with benefits marketplace",s:3},{l:"Combine with wellness and preventative services",s:3},{l:"Combine with insurance and care credits",s:4}],
    ena: [{l:"HR / admin training",s:3},{l:"Provider workflow training",s:4},{l:"Partner demos",s:4},{l:"Support scripts and FAQs",s:4}],
    acq: [{l:"Healthcare-led employer positioning",s:3},{l:"Partner customer sell-in",s:1},{l:"Campaign support",s:3},{l:"Proposals and pilots",s:2}],
    fsa: [{l:"Employee activation",s:4},{l:"Appointment support",s:4},{l:"Case handling",s:4},{l:"Service quality management",s:5},{l:"Escalations and complaints",s:4}],
    bca: [{l:"Subscription or usage billing",s:4},{l:"Co-pay reconciliation",s:5},{l:"Provider reconciliation",s:5},{l:"Commercial reporting",s:4}],
  },
  atomic: {
    pcp: [{l:"Assessment setup",s:5},{l:"Employer-specific scoring / reporting",s:5},{l:"Dashboard configuration",s:4},{l:"Intervention workflow configuration",s:5},{l:"Partner integration",s:3},{l:"Continuous model refinement",s:5}],
    fin: [],
    bp:  [{l:"Use as diagnostic layer across multiple benefits",s:5},{l:"Use as entry point for Flexible Pay, savings, healthcare, or insurance journeys",s:4},{l:"Package into employer wellness offering",s:3}],
    ena: [{l:"Train partner sales and account teams",s:4},{l:"Train on interpretation of outputs",s:4},{l:"Coach enablement",s:4},{l:"Demo and implementation training",s:4}],
    acq: [{l:"Diagnostic-led sales support",s:3},{l:"Pilot support",s:4},{l:"Employer presentations",s:2},{l:"Conversion into other benefits",s:2}],
    fsa: [{l:"Activation",s:4},{l:"Assessments",s:5},{l:"Report delivery",s:5},{l:"Intervention workflows",s:5},{l:"Support and QA",s:4}],
    bca: [{l:"Per employee or per assessment charging",s:5},{l:"Reconciliation",s:4},{l:"Usage reporting",s:4},{l:"Revenue-share reporting",s:4}],
  },
};

const PRODUCTS = [
  { id:"perksco", label:"Perks & Co",    weights:{pcp:20,fin:10,bp:15,ena:10,acq:20,fsa:15,bca:10} },
  { id:"flexpay", label:"Flexible Pay",  weights:{pcp:15,fin:30,bp:10,ena:10,acq:15,fsa:10,bca:10} },
  { id:"savings",  label:"Savings Club", weights:{pcp:20,fin:10,bp:15,ena:10,acq:15,fsa:20,bca:10} },
  { id:"simcare", label:"SimpleCare",    weights:{pcp:20,fin:15,bp:10,ena:10,acq:15,fsa:20,bca:10} },
  { id:"atomic",  label:"Atomic",        weights:{pcp:30,fin:0, bp:20,ena:15,acq:10,fsa:15,bca:10} },
];

const SCORE_LABELS = {1:"Mostly Partner",2:"Partner-led, Level support",3:"Shared",4:"Level-led, Partner support",5:"Fully Level"};
const SCORE_COLORS = {0:"#94a3b8",1:"#ef4444",2:"#f97316",3:"#eab308",4:"#22c55e",5:"#10b981"};

function initChecked() {
  const c = {};
  PRODUCTS.forEach(p => {
    c[p.id] = {};
    STAGES.forEach(st => { c[p.id][st.key] = (ACTIVITIES[p.id][st.key]||[]).map(()=>true); });
  });
  return c;
}

function computeScore(acts, chk) {
  if (!acts || acts.length === 0) return 0;
  if (chk.every(v => !v)) return 1;
  // Deselected activities count as 1 (partner-owned), not excluded
  const total = acts.reduce((sum, a, i) => sum + (chk[i] ? a.s : 1), 0);
  return Math.min(5, Math.max(1, Math.round(total / acts.length)));
}

export default function MinetCommercials() {
  const [active,   setActive]   = useState("perksco");
  const [checked,  setChecked]  = useState(initChecked);
  const [expanded, setExpanded] = useState({});

  const product = PRODUCTS.find(p=>p.id===active);

  const getScore = useCallback((stageKey) => {
    const acts = ACTIVITIES[active][stageKey]||[];
    const chk  = checked[active][stageKey]||[];
    return computeScore(acts, chk);
  }, [active, checked]);

  function calcShares() {
    let lt=0, pt=0;
    const rows = STAGES.map(st => {
      const w = product.weights[st.key];
      if (w===0) return {key:st.key,weight:0,score:0,level:0,partner:0,na:true};
      const sc = getScore(st.key);
      const level   = +(w*(sc/5)).toFixed(2);
      const partner = +(w*(1-sc/5)).toFixed(2);
      lt+=level; pt+=partner;
      return {key:st.key,weight:w,score:sc,level,partner,na:false};
    });
    return {rows, levelTotal:+lt.toFixed(2), partnerTotal:+pt.toFixed(2)};
  }

  const {rows, levelTotal, partnerTotal} = calcShares();

  function toggleExpand(key) { setExpanded(p=>({...p,[active+key]:!p[active+key]})); }

  function toggleActivity(stageKey, idx) {
    setChecked(prev => {
      const arr = [...prev[active][stageKey]];
      arr[idx] = !arr[idx];
      return {...prev,[active]:{...prev[active],[stageKey]:arr}};
    });
  }

  function toggleAll(stageKey, val) {
    setChecked(prev => ({...prev,[active]:{...prev[active],[stageKey]:prev[active][stageKey].map(()=>val)}}));
  }

  function resetProduct() {
    setChecked(prev => {
      const r={};
      STAGES.forEach(st=>{ r[st.key]=(ACTIVITIES[active][st.key]||[]).map(()=>true); });
      return {...prev,[active]:r};
    });
  }

  return (
    <div style={{fontFamily:"'Inter',system-ui,sans-serif",background:"#f8fafc",minHeight:"100vh",padding:"24px 16px"}}>
      <div style={{maxWidth:900,margin:"0 auto"}}>

        {/* Header */}
        <div style={{marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <img src="https://d2xts8p0gw7i1c.cloudfront.net/global_assets/icons/Level Logo Blue White.svg" alt="Level" style={{height:32,width:"auto"}} />
            <h1 style={{margin:0,fontSize:20,fontWeight:700,color:"#0f172a"}}>Commercial Model Framework</h1>
          </div>
          <p style={{margin:0,fontSize:13,color:"#64748b"}}>Revenue share calculator · Select activities per stage to derive ownership scores and revenue splits</p>
        </div>

        {/* Product Tabs */}
        <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
          {PRODUCTS.map(p=>(
            <button key={p.id} onClick={()=>setActive(p.id)} style={{
              padding:"7px 14px",borderRadius:8,border:"1.5px solid",
              borderColor:active===p.id?"#6366f1":"#e2e8f0",
              background:active===p.id?"#6366f1":"#fff",
              color:active===p.id?"#fff":"#334155",
              fontWeight:600,fontSize:13,cursor:"pointer"
            }}>{p.label}</button>
          ))}
        </div>

        {/* Summary Cards */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
          {[
            {label:"Level Revenue Share",   value:`${levelTotal.toFixed(1)}%`,   color:"#6366f1",bg:"#eef2ff"},
            {label:"Partner Revenue Share", value:`${partnerTotal.toFixed(1)}%`, color:"#0ea5e9",bg:"#f0f9ff"},
            {label:"Active Stages",         value:`${rows.filter(r=>!r.na).length} / 7`, color:"#10b981",bg:"#f0fdf4"},
          ].map(c=>(
            <div key={c.label} style={{background:c.bg,borderRadius:12,padding:"14px 16px",border:`1px solid ${c.color}22`}}>
              <div style={{fontSize:11,color:c.color,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>{c.label}</div>
              <div style={{fontSize:28,fontWeight:800,color:c.color}}>{c.value}</div>
            </div>
          ))}
        </div>

        {/* Split Bar */}
        <div style={{marginBottom:16,background:"#fff",borderRadius:12,padding:"14px 16px",border:"1px solid #e2e8f0"}}>
          <div style={{fontSize:12,color:"#64748b",fontWeight:600,marginBottom:8}}>Overall Revenue Split</div>
          <div style={{display:"flex",height:10,borderRadius:6,overflow:"hidden"}}>
            <div style={{width:`${levelTotal}%`,background:"linear-gradient(90deg,#6366f1,#8b5cf6)",transition:"width .3s"}}/>
            <div style={{flex:1,background:"linear-gradient(90deg,#38bdf8,#0ea5e9)"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:12}}>
            <span style={{color:"#6366f1",fontWeight:600}}>● Level {levelTotal.toFixed(1)}%</span>
            <span style={{color:"#0ea5e9",fontWeight:600}}>Partner {partnerTotal.toFixed(1)}% ●</span>
          </div>
        </div>

        {/* Stage Rows */}
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          {rows.map((row,i)=>{
            const st      = STAGES[i];
            const acts    = ACTIVITIES[active][st.key]||[];
            const chk     = checked[active][st.key]||[];
            const selCount= chk.filter(Boolean).length;
            const isOpen  = !!expanded[active+st.key];
            const na      = row.na;

            return (
              <div key={st.key} style={{background:"#fff",borderRadius:12,border:"1px solid #e2e8f0",overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 58px 220px 72px 72px",gap:0,padding:"13px 14px",alignItems:"center"}}>

                  {/* Stage name */}
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:na?"#94a3b8":"#1e293b"}}>{st.label}</div>
                    {!na && acts.length>0 && (
                      <div style={{display:"flex",alignItems:"center",gap:5,marginTop:3}}>
                        <span style={{fontSize:11,color:"#94a3b8"}}>{selCount}/{acts.length} activities selected</span>
                        <button onClick={()=>toggleExpand(st.key)} style={{
                          width:18,height:18,borderRadius:4,border:"1px solid #e2e8f0",
                          background:isOpen?"#f1f5f9":"#fff",cursor:"pointer",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          color:"#94a3b8",fontSize:8,fontWeight:700,padding:0,flexShrink:0
                        }}>{isOpen?"▲":"▼"}</button>
                      </div>
                    )}
                  </div>

                  {/* Weight */}
                  <div style={{fontSize:13,fontWeight:700,color:na?"#94a3b8":"#6366f1"}}>
                    {na?"—":`${row.weight}%`}
                  </div>

                  {/* Score indicator (derived, read-only) */}
                  <div style={{paddingRight:12}}>
                    {na ? (
                      <span style={{fontSize:12,color:"#94a3b8",fontStyle:"italic"}}>Not applicable</span>
                    ) : selCount===0 ? (
                      <span style={{fontSize:12,color:"#f97316",fontStyle:"italic"}}>No activities selected</span>
                    ) : (
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{fontSize:10,color:"#94a3b8"}}>Partner</span>
                          <span style={{fontSize:10,color:SCORE_COLORS[row.score],fontWeight:700}}>{SCORE_LABELS[row.score]}</span>
                          <span style={{fontSize:10,color:"#94a3b8"}}>Level</span>
                        </div>
                        {/* Track */}
                        <div style={{position:"relative",height:6,borderRadius:3,background:"#e2e8f0",marginBottom:4}}>
                          <div style={{
                            position:"absolute",left:0,top:0,height:"100%",borderRadius:3,
                            width:`${((row.score-1)/4)*100}%`,
                            background:`linear-gradient(90deg,${SCORE_COLORS[1]},${SCORE_COLORS[row.score]})`,
                            transition:"width .3s"
                          }}/>
                          {/* Thumb */}
                          <div style={{
                            position:"absolute",top:"50%",
                            left:`calc(${((row.score-1)/4)*100}% - 7px)`,
                            transform:"translateY(-50%)",
                            width:14,height:14,borderRadius:"50%",
                            background:SCORE_COLORS[row.score],
                            border:"2px solid #fff",
                            boxShadow:"0 1px 3px rgba(0,0,0,.2)",
                            transition:"left .3s"
                          }}/>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between"}}>
                          {[1,2,3,4,5].map(n=>(
                            <span key={n} style={{fontSize:9,color:n===row.score?SCORE_COLORS[n]:"#cbd5e1",fontWeight:n===row.score?700:400}}>{n}</span>
                          ))}
                        </div>
                        <div style={{fontSize:10,color:"#94a3b8",marginTop:3,textAlign:"center"}}>Score derived from selected activities</div>
                      </div>
                    )}
                  </div>

                  {/* Level share */}
                  <div style={{textAlign:"right",paddingRight:8}}>
                    {na?<span style={{color:"#94a3b8"}}>—</span>:(
                      <div>
                        <div style={{fontSize:14,fontWeight:700,color:"#6366f1"}}>{row.level.toFixed(1)}%</div>
                        <div style={{height:3,borderRadius:2,background:"#e0e7ff",marginTop:3}}>
                          <div style={{height:"100%",borderRadius:2,background:"#6366f1",width:`${(row.level/row.weight)*100}%`,transition:"width .3s"}}/>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Partner share */}
                  <div style={{textAlign:"right"}}>
                    {na?<span style={{color:"#94a3b8"}}>—</span>:(
                      <div>
                        <div style={{fontSize:14,fontWeight:700,color:"#0ea5e9"}}>{row.partner.toFixed(1)}%</div>
                        <div style={{height:3,borderRadius:2,background:"#e0f2fe",marginTop:3}}>
                          <div style={{height:"100%",borderRadius:2,background:"#0ea5e9",width:`${(row.partner/row.weight)*100}%`,transition:"width .3s"}}/>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Activity Panel */}
                {!na && isOpen && acts.length>0 && (
                  <div style={{borderTop:"1px solid #f1f5f9",background:"#fafbff",padding:"12px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <div>
                        <span style={{fontSize:12,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.04em"}}>Activities</span>
                        <span style={{fontSize:11,color:"#94a3b8",marginLeft:8}}>Each activity carries an implied ownership score (shown in brackets) · toggle to include / exclude</span>
                      </div>
                      <div style={{display:"flex",gap:6,flexShrink:0}}>
                        <button onClick={()=>toggleAll(st.key,true)}  style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid #c7d2fe",background:"#eef2ff",color:"#6366f1",fontWeight:600,cursor:"pointer"}}>Select all</button>
                        <button onClick={()=>toggleAll(st.key,false)} style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid #e2e8f0",background:"#f8fafc",color:"#94a3b8",fontWeight:600,cursor:"pointer"}}>Clear all</button>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 16px"}}>
                      {acts.map((act,idx)=>(
                        <label key={idx} style={{
                          display:"flex",alignItems:"center",gap:8,cursor:"pointer",
                          padding:"7px 10px",borderRadius:8,
                          background:chk[idx]?"#fff":"#f8fafc",
                          border:`1px solid ${chk[idx]?"#c7d2fe":"#e2e8f0"}`,
                          transition:"all .1s"
                        }}>
                          <input type="checkbox" checked={!!chk[idx]} onChange={()=>toggleActivity(st.key,idx)}
                            style={{accentColor:"#6366f1",cursor:"pointer",flexShrink:0}}/>
                          <span style={{flex:1,fontSize:12.5,color:chk[idx]?"#1e293b":"#94a3b8",textDecoration:chk[idx]?"none":"line-through"}}>{act.l}</span>
                          {/* Score badge */}
                          <span style={{
                            fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:4,flexShrink:0,
                            background:`${SCORE_COLORS[act.s]}18`,
                            color:SCORE_COLORS[act.s],
                            border:`1px solid ${SCORE_COLORS[act.s]}44`
                          }}>{act.s}/5</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Totals */}
        <div style={{background:"#1e293b",borderRadius:12,padding:"14px 16px",display:"grid",gridTemplateColumns:"1fr 58px 220px 72px 72px",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>Total</div>
          <div style={{fontSize:13,fontWeight:700,color:"#94a3b8"}}>100%</div>
          <div/>
          <div style={{textAlign:"right",paddingRight:8,fontSize:16,fontWeight:800,color:"#a5b4fc"}}>{levelTotal.toFixed(1)}%</div>
          <div style={{textAlign:"right",fontSize:16,fontWeight:800,color:"#7dd3fc"}}>{partnerTotal.toFixed(1)}%</div>
        </div>

        {/* Legend + Reset */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {Object.entries(SCORE_LABELS).map(([k,v])=>(
              <span key={k} style={{fontSize:11,background:"#f1f5f9",border:`1px solid ${SCORE_COLORS[k]}44`,borderRadius:6,padding:"3px 8px",color:SCORE_COLORS[k],fontWeight:600}}>{k} = {v}</span>
            ))}
          </div>
          <button onClick={resetProduct} style={{padding:"7px 14px",borderRadius:8,border:"1.5px solid #e2e8f0",background:"#fff",color:"#64748b",fontWeight:600,fontSize:13,cursor:"pointer"}}>↺ Reset {product.label}</button>
        </div>

      </div>
    </div>
  );
}
